import type { LucideIcon } from 'lucide-react';
import type { ReactNode } from 'react';
import { memo, useMemo, useState } from 'react';
import { cn } from '@/shared/lib/cn';
import { Badge, type BadgeProps } from '@/shared/ui/badge';
import { Popover, PopoverContent, PopoverTrigger } from '@/shared/ui/popover';
import { TooltipCopyContent, type TooltipCopyLine } from '@/shared/components/tooltip-copy-content';

interface BadgeTooltipProps {
  label: string;
  icon?: LucideIcon;
  /** Linhas copiáveis — preferido. */
  copyLines?: TooltipCopyLine[];
  /** Conteúdo livre (sem copiar estruturado). */
  tooltip?: ReactNode;
  variant?: BadgeProps['variant'];
  className?: string;
  disabled?: boolean;
  size?: 'sm' | 'md';
  wide?: boolean;
  style?: React.CSSProperties;
}

/** Badge shadcn com tooltip animado e botões de copiar. */
export const BadgeTooltip = memo(function BadgeTooltip({
  label,
  icon: Icon,
  copyLines,
  tooltip,
  variant = 'secondary',
  className,
  disabled,
  size = 'md',
  wide,
  style,
}: BadgeTooltipProps) {
  const [open, setOpen] = useState(false);

  const badgeWidth =
    size === 'sm'
      ? 'min-w-[4.5rem]'
      : wide
        ? 'w-max whitespace-nowrap'
        : 'min-w-[5.5rem]';

  const badge = (
    <Badge
      variant={variant}
      style={style}
      className={cn(
        'h-7 justify-center gap-1.5 border font-medium shadow-sm transition-all duration-200 ease-out',
        badgeWidth,
        size === 'sm' ? 'px-2 text-[11px]' : 'px-2.5 text-xs',
        !disabled && 'cursor-default hover:scale-[1.03] hover:shadow-md active:scale-[0.99]',
        disabled && 'opacity-45',
        className,
      )}
    >
      {Icon ? <Icon className="size-3.5 shrink-0 opacity-90" /> : null}
      <span className={wide ? undefined : 'truncate'}>{label}</span>
    </Badge>
  );

  const tooltipContent = useMemo(() => {
    if (!open) return null;
    if (copyLines && copyLines.length > 0) return <TooltipCopyContent lines={copyLines} />;
    return tooltip ?? null;
  }, [open, copyLines, tooltip]);

  const hasTooltip = Boolean((copyLines && copyLines.length > 0) || tooltip);

  return (
    <div
      className={cn('flex shrink-0 justify-center', className)}
      onClick={(e) => e.stopPropagation()}
      onKeyDown={(e) => e.stopPropagation()}
    >
      {disabled || !hasTooltip ? (
        badge
      ) : (
        // Popover (não Tooltip) de propósito: Radix Tooltip ignora toque por
        // design (só abre em hover/foco), o que tornaria telefone/e-mail/
        // outros `copyLines` inacessíveis em mobile. Popover abre com clique
        // e toque igualmente.
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <button type="button" className="inline-flex touch-manipulation rounded-full" aria-label={label}>
              {badge}
            </button>
          </PopoverTrigger>
          <PopoverContent side="top" align="center" className="max-w-[20rem] p-3">
            {tooltipContent ?? label}
          </PopoverContent>
        </Popover>
      )}
    </div>
  );
});
